import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FileOpenOutlinedIcon from "@mui/icons-material/FileOpenOutlined";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  participantsApi,
  type Participant,
  type ParticipantTask,
  type ParticipantUser,
} from "../api/participants";
import { qk } from "../../../../lib/queryKeys";
import { ROUTES } from "../../../../shared/utils";
import { useClientScope } from "../../../../shared/contexts";
import { SectionCard } from "../../settings/components/SectionCard";

const STATUS_OPTIONS = ["Active", "Pending", "On hold", "Completed"];
const FLAGGED_OPTIONS = ["None", "Needs follow-up", "Escalated"];
const GENDER_OPTIONS = ["Female", "Male", "Non-binary", "Prefer not to say"];
const DEFAULT_COLOUR_TAG = "#64748b";

const emptyParticipant: Participant = {
  id: "", user_id: "", title: "", first_name: "", surname: "",
  gender: "", email: "", contact_number: "",
  address_line_1: "", address_line_2: "", address_line_3: "",
  town: "", postcode: "", company: "", job_role: "", status: "",
  current_whoop_user: false, whoop_received: false,
  whoop_unite_email_sent: false, whoop_unite_connected: false,
  peak4_app: false, peak4_api_connected: false,
  date_started: "", date_added_to_lms: "", date_to_be_removed_from_lms: "",
  color_tag: "", flagged: "", compliance_asset_id: "", notes: "",
  using_own_device: false,
};

const toDateInputValue = (value?: string | null) => (!value ? "" : value.slice(0, 10));

const parseDateValue = (value?: string | null): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const resolveColourTag = (value?: string | null) => {
  if (!value) return DEFAULT_COLOUR_TAG;
  const n = value.trim();
  return /^#[0-9a-fA-F]{6}$/.test(n) ? n : DEFAULT_COLOUR_TAG;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

// ---------------------------------------------------------------------------
// Shared settings-row toggle (label + description + Switch)
// ---------------------------------------------------------------------------
const FlagRow: React.FC<{
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.75 }}>
    <Typography variant="body2" sx={{ flex: 1 }}>{label}</Typography>
    <Switch
      size="small"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
  </Stack>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ParticipantDetail: React.FC = () => {
  const { participantId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = participantId === "new";
  const { effectiveClientId, isCentralAdmin } = useClientScope();

  const [formState, setFormState] = useState<Participant>(emptyParticipant);
  const [deviceIds, setDeviceIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState("profile");
  const [interactionForm, setInteractionForm] = useState({ occurred_at: "", type: "", summary: "", body: "" });
  const [taskForm, setTaskForm] = useState({ title: "", description: "", due_date: "" });
  const [taskRows, setTaskRows] = useState<ParticipantTask[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const set = <K extends keyof Participant>(key: K, value: Participant[K]) =>
    setFormState((prev) => ({ ...prev, [key]: value }));

  // ── Queries ──────────────────────────────────────────────────────────────

  const participantQuery = useQuery({
    queryKey: qk.participantDetail(participantId ?? null),
    queryFn: () => participantsApi.get(participantId as string),
    enabled: Boolean(participantId && !isNew),
  });

  const devicesQuery = useQuery({
    queryKey: qk.devices(),
    queryFn: () => participantsApi.listDevices(true),
  });

  const usersQuery = useQuery({
    queryKey: qk.participantUsers(isCentralAdmin ? effectiveClientId ?? null : null),
    queryFn: () => participantsApi.listUsers(isCentralAdmin ? effectiveClientId ?? null : null),
  });

  const interactionsQuery = useQuery({
    queryKey: qk.participantInteractions(participantId ?? null),
    queryFn: () => participantsApi.listInteractions(participantId as string),
    enabled: Boolean(participantId && !isNew),
  });

  const documentsQuery = useQuery({
    queryKey: qk.participantDocuments(participantId ?? null),
    queryFn: () => participantsApi.listDocuments(participantId as string),
    enabled: Boolean(participantId && !isNew),
  });

  const tasksQuery = useQuery({
    queryKey: qk.participantTasks(participantId ?? null),
    queryFn: () => participantsApi.listTasks(participantId as string),
    enabled: Boolean(participantId && !isNew),
  });

  useEffect(() => {
    if (!participantQuery.data?.data) return;
    const { devices, ...participant } = participantQuery.data.data;
    setFormState({
      ...participant,
      date_started: toDateInputValue(participant.date_started),
      date_added_to_lms: toDateInputValue(participant.date_added_to_lms),
      date_to_be_removed_from_lms: toDateInputValue(participant.date_to_be_removed_from_lms),
    });
    setDeviceIds(devices?.map((d) => d.id) ?? []);
  }, [participantQuery.data]);

  useEffect(() => {
    if (!tasksQuery.data?.data) return;
    setTaskRows(tasksQuery.data.data);
  }, [tasksQuery.data]);

  // ── Mutations ─────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (payload: Participant) =>
      participantsApi.create({ ...payload, user_id: payload.user_id || null, device_ids: deviceIds }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["participants"] });
      navigate(`${ROUTES.PARTICIPANTS}/${response.data.id}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Participant) =>
      participantsApi.update(participantId as string, { ...payload, user_id: payload.user_id || null, device_ids: deviceIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.participantDetail(participantId ?? null) });
      queryClient.invalidateQueries({ queryKey: ["participants"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => participantsApi.remove(participantId as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["participants"] });
      navigate(ROUTES.PARTICIPANTS);
    },
  });

  const createInteractionMutation = useMutation({
    mutationFn: (payload: typeof interactionForm) =>
      participantsApi.createInteraction(participantId as string, payload),
    onSuccess: () => {
      setInteractionForm({ occurred_at: "", type: "", summary: "", body: "" });
      queryClient.invalidateQueries({ queryKey: qk.participantInteractions(participantId ?? null) });
    },
  });

  const createDocumentMutation = useMutation({
    mutationFn: (payload: { file_name: string; mime_type: string; size_bytes: number; storage_key: string }) =>
      participantsApi.createDocument(participantId as string, payload),
    onSuccess: () => {
      setUploadFile(null);
      queryClient.invalidateQueries({ queryKey: qk.participantDocuments(participantId ?? null) });
    },
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: (documentId: string) => participantsApi.deleteDocument(participantId as string, documentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.participantDocuments(participantId ?? null) }),
  });

  const createTaskMutation = useMutation({
    mutationFn: (payload: typeof taskForm) =>
      participantsApi.createTask(participantId as string, payload),
    onSuccess: () => {
      setTaskForm({ title: "", description: "", due_date: "" });
      queryClient.invalidateQueries({ queryKey: qk.participantTasks(participantId ?? null) });
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: Partial<ParticipantTask> }) =>
      participantsApi.updateTask(participantId as string, taskId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.participantTasks(participantId ?? null) }),
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (taskId: string) => participantsApi.deleteTask(participantId as string, taskId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.participantTasks(participantId ?? null) }),
  });

  const applyDefaultsMutation = useMutation({
    mutationFn: () => participantsApi.applyDefaultTasks(participantId as string),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.participantTasks(participantId ?? null) }),
  });

  const handleSave = () => {
    if (isNew) createMutation.mutate(formState);
    else updateMutation.mutate(formState);
  };

  const handleUpload = async () => {
    if (!uploadFile || !participantId) return;
    setUploadError(null);
    try {
      const uploadMeta = await participantsApi.createDocumentUploadUrl(participantId, {
        file_name: uploadFile.name,
        mime_type: uploadFile.type || "application/octet-stream",
      });
      const res = await fetch(uploadMeta.upload_url, {
        method: "PUT",
        headers: { "Content-Type": uploadFile.type || "application/octet-stream" },
        body: uploadFile,
      });
      if (!res.ok) throw new Error("Upload failed");
      createDocumentMutation.mutate({
        file_name: uploadFile.name,
        mime_type: uploadFile.type || "application/octet-stream",
        size_bytes: uploadFile.size,
        storage_key: uploadMeta.storage_key,
      });
    } catch {
      setUploadError("Failed to upload document.");
    }
  };

  const handleTaskUpdate = (taskId: string, payload: Partial<ParticipantTask>) => {
    const existing = taskRows.find((t) => t.id === taskId) ?? tasksQuery.data?.data?.find((t) => t.id === taskId);
    updateTaskMutation.mutate({ taskId, payload: existing ? { ...existing, ...payload } : payload });
  };

  const updateTaskLocal = (taskId: string, updates: Partial<ParticipantTask>) =>
    setTaskRows((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...updates } : t)));

  const commitTask = (taskId: string) => {
    const task = taskRows.find((t) => t.id === taskId);
    if (task) handleTaskUpdate(taskId, task);
  };

  const deviceOptions = devicesQuery.data?.data ?? [];
  const userOptions = useMemo(() => usersQuery.data?.data ?? [], [usersQuery.data]);
  const selectedUser = useMemo(
    () => (formState.user_id ? userOptions.find((u) => u.id === formState.user_id) ?? null : null),
    [formState.user_id, userOptions]
  );

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const fullName = `${formState.first_name ?? ""} ${formState.surname ?? ""}`.trim();
  const avatarInitials = `${formState.first_name?.[0] ?? ""}${formState.surname?.[0] ?? ""}`.toUpperCase() || "P";

  const doneCount = taskRows.filter((t) => t.status === "done").length;
  const hasSurveyData = Boolean(!isNew && formState.signup_answers?.length);

  const tabDefs = [
    { value: "profile", label: "Profile" },
    { value: "programme", label: "Programme" },
    ...(hasSurveyData ? [{ value: "survey", label: "Survey data" }] : []),
    ...(!isNew ? [
      { value: "interactions", label: "Interactions" },
      { value: "documents", label: "Documents" },
      { value: "tasks", label: taskRows.length ? `Tasks (${doneCount}/${taskRows.length})` : "Tasks" },
    ] : []),
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 2.5 } }}>
      {/* Breadcrumb */}
      <Button
        size="small"
        variant="text"
        startIcon={<ArrowBackIcon fontSize="small" />}
        onClick={() => navigate(ROUTES.PARTICIPANTS)}
        sx={{ mb: 1.5, color: "text.secondary", pl: 0 }}
      >
        Participants
      </Button>

      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        spacing={1.5}
        mb={2.5}
      >
        {!isNew && (
          <Avatar
            sx={{
              width: 44, height: 44,
              bgcolor: "primary.main",
              fontWeight: 700, fontSize: 16,
              flexShrink: 0,
            }}
          >
            {avatarInitials}
          </Avatar>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} noWrap>
            {isNew ? "New participant" : fullName || "Participant"}
          </Typography>
          {!isNew && (
            <Typography variant="caption" color="text.secondary">
              Updated {formatDateTime(formState.updated_at)}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {!isNew && formState.status && (
            <Chip label={formState.status} size="small" />
          )}
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon fontSize="small" />}
          >
            Save
          </Button>
          {!isNew && (
            <Tooltip title="Delete participant">
              <IconButton
                size="small"
                color="error"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  if (window.confirm("Delete this participant? This cannot be undone.")) {
                    deleteMutation.mutate();
                  }
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {participantQuery.isError && !isNew && (
        <Alert severity="error" sx={{ mb: 2 }}>Failed to load participant.</Alert>
      )}

      {/* Tab bar */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          mb: 3,
          minHeight: 40,
          "& .MuiTab-root": { minHeight: 40, py: 0 },
        }}
      >
        {tabDefs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={tab.label}
            sx={{ textTransform: "none", fontWeight: 500, fontSize: 13 }}
          />
        ))}
      </Tabs>

      {/* ── Profile tab ── */}
      {activeTab === "profile" && (
        <Grid container spacing={3}>
          {/* Identity */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="Identity" description="Personal and account details">
              <Stack spacing={2}>
                {!isNew && (
                  <TextField
                    label="Participant ID"
                    value={formState.id}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                )}
                <Autocomplete
                  options={userOptions}
                  value={selectedUser}
                  onChange={(_, value: ParticipantUser | null) => {
                    setFormState((prev) => ({
                      ...prev,
                      user_id: value?.id ?? "",
                      email: value?.email ?? prev.email,
                      first_name: value?.first_name ?? prev.first_name,
                      surname: value?.last_name ?? prev.surname,
                    }));
                  }}
                  getOptionLabel={(option) =>
                    option ? `${option.first_name ?? ""} ${option.last_name ?? ""} (${option.email})`.trim() : ""
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Linked user" placeholder="Select a user" size="small" />
                  )}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Title"
                    value={formState.title || ""}
                    onChange={(e) => set("title", e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    select
                    label="Gender"
                    value={formState.gender || ""}
                    onChange={(e) => set("gender", e.target.value)}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    {GENDER_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </TextField>
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="First name"
                    required
                    value={formState.first_name}
                    onChange={(e) => set("first_name", e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Surname"
                    required
                    value={formState.surname}
                    onChange={(e) => set("surname", e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Stack>
              </Stack>
            </SectionCard>
          </Grid>

          {/* Contact */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="Contact" description="Email, phone and address">
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Email address"
                    value={formState.email || ""}
                    onChange={(e) => set("email", e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Contact number"
                    value={formState.contact_number || ""}
                    onChange={(e) => set("contact_number", e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="Address line 1"
                  value={formState.address_line_1 || ""}
                  onChange={(e) => set("address_line_1", e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Address line 2"
                  value={formState.address_line_2 || ""}
                  onChange={(e) => set("address_line_2", e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Address line 3"
                  value={formState.address_line_3 || ""}
                  onChange={(e) => set("address_line_3", e.target.value)}
                  size="small"
                  fullWidth
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Town"
                    value={formState.town || ""}
                    onChange={(e) => set("town", e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Postcode"
                    value={formState.postcode || ""}
                    onChange={(e) => set("postcode", e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Stack>
              </Stack>
            </SectionCard>
          </Grid>

          {/* Organisation */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="Organisation" description="Company, role and programme status">
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Company"
                    value={formState.company || ""}
                    onChange={(e) => set("company", e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Job role"
                    value={formState.job_role || ""}
                    onChange={(e) => set("job_role", e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Stack>
                <TextField
                  select
                  label="Status"
                  value={formState.status || ""}
                  onChange={(e) => set("status", e.target.value)}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="">None</MenuItem>
                  {STATUS_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
              </Stack>
            </SectionCard>
          </Grid>

          {/* Notes */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="Notes" description="General notes about this participant">
              <TextField
                label="Notes"
                value={formState.notes || ""}
                onChange={(e) => set("notes", e.target.value)}
                multiline
                minRows={5}
                size="small"
                fullWidth
              />
            </SectionCard>
          </Grid>
        </Grid>
      )}

      {/* ── Programme tab ── */}
      {activeTab === "programme" && (
        <Grid container spacing={3}>
          {/* Wearable & app flags */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="Wearable & app" description="Device and connectivity status">
              <Stack divider={<Divider />} spacing={0}>
                <FlagRow label="Current WHOOP user" checked={Boolean(formState.current_whoop_user)} onChange={(v) => set("current_whoop_user", v)} />
                <FlagRow label="WHOOP received" checked={Boolean(formState.whoop_received)} onChange={(v) => set("whoop_received", v)} />
                <FlagRow label="WHOOP Unite email sent" checked={Boolean(formState.whoop_unite_email_sent)} onChange={(v) => set("whoop_unite_email_sent", v)} />
                <FlagRow label="WHOOP Unite connected" checked={Boolean(formState.whoop_unite_connected)} onChange={(v) => set("whoop_unite_connected", v)} />
                <FlagRow label="PEAK4 app set up" checked={Boolean(formState.peak4_app)} onChange={(v) => set("peak4_app", v)} />
                <FlagRow label="PEAK4 API connected" checked={Boolean(formState.peak4_api_connected)} onChange={(v) => set("peak4_api_connected", v)} />
                <FlagRow label="Using own device" checked={Boolean(formState.using_own_device)} onChange={(v) => set("using_own_device", v)} />
              </Stack>
            </SectionCard>
          </Grid>

          {/* Programme dates */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="Programme dates">
              <Stack spacing={2}>
                <DatePicker
                  label="Date started"
                  format="DD/MM/YYYY"
                  value={parseDateValue(formState.date_started)}
                  onChange={(v) => set("date_started", v?.isValid() ? v.format("YYYY-MM-DD") : "")}
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                />
                <DatePicker
                  label="Date added to LMS"
                  format="DD/MM/YYYY"
                  value={parseDateValue(formState.date_added_to_lms)}
                  onChange={(v) => set("date_added_to_lms", v?.isValid() ? v.format("YYYY-MM-DD") : "")}
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                />
                <DatePicker
                  label="Date to be removed from LMS"
                  format="DD/MM/YYYY"
                  value={parseDateValue(formState.date_to_be_removed_from_lms)}
                  onChange={(v) => set("date_to_be_removed_from_lms", v?.isValid() ? v.format("YYYY-MM-DD") : "")}
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                />
              </Stack>
            </SectionCard>
          </Grid>

          {/* Tags & devices */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="Tags & devices" description="Flags, colour coding and assigned devices">
              <Stack spacing={2}>
                <TextField
                  select
                  label="Flagged"
                  value={formState.flagged || ""}
                  onChange={(e) => set("flagged", e.target.value)}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="">None</MenuItem>
                  {FLAGGED_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="Colour tag"
                    value={formState.color_tag || ""}
                    onChange={(e) => set("color_tag", e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <Box
                    sx={{
                      width: 36, height: 36,
                      borderRadius: 1,
                      border: "2px solid",
                      borderColor: "divider",
                      bgcolor: resolveColourTag(formState.color_tag),
                      flexShrink: 0,
                      cursor: "pointer",
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <input
                      type="color"
                      value={resolveColourTag(formState.color_tag)}
                      onChange={(e) => set("color_tag", e.target.value)}
                      style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }}
                    />
                  </Box>
                </Stack>
                <TextField
                  label="Compliance asset ID"
                  value={formState.compliance_asset_id || ""}
                  onChange={(e) => set("compliance_asset_id", e.target.value)}
                  size="small"
                  fullWidth
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Assigned devices</InputLabel>
                  <Select
                    label="Assigned devices"
                    multiple
                    value={deviceIds}
                    onChange={(e) =>
                      setDeviceIds(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)
                    }
                    renderValue={(selected) =>
                      deviceOptions
                        .filter((d) => selected.includes(d.id))
                        .map((d) => d.name)
                        .join(", ")
                    }
                  >
                    {deviceOptions.map((d) => (
                      <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </SectionCard>
          </Grid>
        </Grid>
      )}

      {/* ── Survey data tab ── */}
      {activeTab === "survey" && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <SectionCard title="Sign-up answers" description="Responses from pre-registration and onboarding">
              {formState.signup_answers?.length ? (
                <Stack spacing={0} divider={<Divider />}>
                  {formState.signup_answers.map((answer) => (
                    <Box key={`${answer.source}-${answer.question_id}`} sx={{ py: 1.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                          {answer.title}
                        </Typography>
                        <Chip
                          label={answer.source === "pre_registration" ? "Pre-reg" : "Onboarding"}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 11, height: 20 }}
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {answer.answer || "—"}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                  <Typography variant="body2">No sign-up data captured yet.</Typography>
                </Box>
              )}
            </SectionCard>
          </Grid>
        </Grid>
      )}

      {/* ── Interactions tab ── */}
      {activeTab === "interactions" && (
        <Stack spacing={3}>
          {/* Add form */}
          <SectionCard
            title="Log interaction"
            description="Record a call, meeting, or email"
            action={
              <Button
                variant="contained"
                size="small"
                startIcon={createInteractionMutation.isPending ? <CircularProgress size={14} color="inherit" /> : <ForumOutlinedIcon fontSize="small" />}
                disabled={!interactionForm.body.trim() || createInteractionMutation.isPending}
                onClick={() => createInteractionMutation.mutate(interactionForm)}
              >
                Add
              </Button>
            }
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Date & time"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  value={interactionForm.occurred_at}
                  onChange={(e) => setInteractionForm((p) => ({ ...p, occurred_at: e.target.value }))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  label="Type"
                  value={interactionForm.type}
                  onChange={(e) => setInteractionForm((p) => ({ ...p, type: e.target.value }))}
                  size="small"
                  fullWidth
                  placeholder="e.g. Call, Email"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField
                  label="Summary"
                  value={interactionForm.summary}
                  onChange={(e) => setInteractionForm((p) => ({ ...p, summary: e.target.value }))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Notes"
                  value={interactionForm.body}
                  onChange={(e) => setInteractionForm((p) => ({ ...p, body: e.target.value }))}
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>
            </Grid>
          </SectionCard>

          {/* List */}
          {interactionsQuery.isLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Stack>
          ) : interactionsQuery.isError ? (
            <Alert severity="error">Failed to load interactions.</Alert>
          ) : interactionsQuery.data?.data?.length ? (
            <Stack spacing={1.5}>
              {interactionsQuery.data.data.map((interaction) => (
                <Box
                  key={interaction.id}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    "&:hover": { borderColor: "primary.light" },
                    transition: "border-color 0.15s",
                  }}
                >
                  <Stack direction="row" alignItems="center" mb={0.75} spacing={1}>
                    {interaction.type && (
                      <Chip label={interaction.type} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                      {formatDateTime(interaction.occurred_at)}
                    </Typography>
                    {(interaction.first_name || interaction.last_name) && (
                      <Typography variant="caption" color="text.secondary">
                        {interaction.first_name} {interaction.last_name}
                      </Typography>
                    )}
                  </Stack>
                  {interaction.summary && (
                    <Typography variant="body2" fontWeight={500} mb={0.5}>
                      {interaction.summary}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                    {interaction.body}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
              <ForumOutlinedIcon sx={{ fontSize: 36, opacity: 0.25, mb: 1 }} />
              <Typography variant="body2">No interactions logged yet.</Typography>
            </Box>
          )}
        </Stack>
      )}

      {/* ── Documents tab ── */}
      {activeTab === "documents" && (
        <Stack spacing={3}>
          <SectionCard title="Upload document">
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <Button variant="outlined" size="small" component="label" startIcon={<AttachFileIcon fontSize="small" />}>
                Choose file
                <input type="file" hidden onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)} />
              </Button>
              <Typography variant="body2" color={uploadFile ? "text.primary" : "text.disabled"} sx={{ flex: 1 }}>
                {uploadFile ? uploadFile.name : "No file chosen"}
              </Typography>
              <Button
                variant="contained"
                size="small"
                disabled={!uploadFile || createDocumentMutation.isPending}
                onClick={handleUpload}
              >
                {createDocumentMutation.isPending ? <CircularProgress size={16} /> : "Upload"}
              </Button>
            </Stack>
            {uploadError && <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>{uploadError}</Alert>}
          </SectionCard>

          {documentsQuery.isLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Stack>
          ) : documentsQuery.isError ? (
            <Alert severity="error">Failed to load documents.</Alert>
          ) : documentsQuery.data?.data?.length ? (
            <Stack spacing={1.5}>
              {documentsQuery.data.data.map((doc) => (
                <Box
                  key={doc.id}
                  sx={{
                    p: 1.75,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <FileOpenOutlinedIcon sx={{ color: "text.disabled", flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} noWrap>{doc.file_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {doc.size_bytes ? `${Math.round(doc.size_bytes / 1024)} KB · ` : ""}{formatDateTime(doc.uploaded_at)}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    {doc.file_url && (
                      <Button
                        variant="outlined"
                        size="small"
                        component="a"
                        href={doc.file_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open
                      </Button>
                    )}
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      onClick={() => deleteDocumentMutation.mutate(doc.id)}
                    >
                      Delete
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : (
            <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
              <AttachFileIcon sx={{ fontSize: 36, opacity: 0.25, mb: 1 }} />
              <Typography variant="body2">No documents uploaded yet.</Typography>
            </Box>
          )}
        </Stack>
      )}

      {/* ── Tasks tab ── */}
      {activeTab === "tasks" && (
        <Stack spacing={3}>
          {/* Add + header */}
          <SectionCard
            title="Add task"
            action={
              <Button
                variant="outlined"
                size="small"
                disabled={applyDefaultsMutation.isPending}
                onClick={() => applyDefaultsMutation.mutate()}
              >
                {applyDefaultsMutation.isPending ? <CircularProgress size={14} /> : "Apply defaults"}
              </Button>
            }
          >
            <Grid container spacing={2} alignItems="flex-end">
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField
                  label="Title"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm((p) => ({ ...p, title: e.target.value }))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  label="Due date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm((p) => ({ ...p, due_date: e.target.value }))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Notes"
                  value={taskForm.description}
                  onChange={(e) => setTaskForm((p) => ({ ...p, description: e.target.value }))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AssignmentOutlinedIcon fontSize="small" />}
                  disabled={!taskForm.title.trim() || createTaskMutation.isPending}
                  onClick={() => createTaskMutation.mutate(taskForm)}
                >
                  {createTaskMutation.isPending ? <CircularProgress size={14} /> : "Add task"}
                </Button>
              </Grid>
            </Grid>
          </SectionCard>

          {/* Progress summary */}
          {taskRows.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {doneCount} of {taskRows.length} completed
              </Typography>
              <Box
                sx={{
                  flex: 1, height: 4, bgcolor: "action.hover",
                  borderRadius: 2, overflow: "hidden", maxWidth: 200,
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${taskRows.length > 0 ? (doneCount / taskRows.length) * 100 : 0}%`,
                    bgcolor: "success.main",
                    borderRadius: 2,
                    transition: "width 0.3s",
                  }}
                />
              </Box>
            </Stack>
          )}

          {/* Task list */}
          {tasksQuery.isLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Stack>
          ) : tasksQuery.isError ? (
            <Alert severity="error">Failed to load tasks.</Alert>
          ) : taskRows.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
              <CheckBoxOutlinedIcon sx={{ fontSize: 36, opacity: 0.25, mb: 1 }} />
              <Typography variant="body2">No tasks yet.</Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {taskRows.map((task) => (
                <Box
                  key={task.id}
                  sx={{
                    p: 1.75,
                    border: "1px solid",
                    borderColor: task.status === "done" ? "success.light" : "divider",
                    borderRadius: 2,
                    opacity: task.status === "done" ? 0.7 : 1,
                    transition: "border-color 0.15s, opacity 0.15s",
                  }}
                >
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Checkbox
                          size="small"
                          checked={task.status === "done"}
                          onChange={(e) => {
                            const next = e.target.checked ? "done" : "todo";
                            updateTaskLocal(task.id, { status: next });
                            handleTaskUpdate(task.id, { status: next });
                          }}
                          sx={{ p: 0, flexShrink: 0 }}
                        />
                        <TextField
                          value={task.title}
                          onChange={(e) => updateTaskLocal(task.id, { title: e.target.value })}
                          onBlur={() => commitTask(task.id)}
                          size="small"
                          fullWidth
                          InputProps={{
                            sx: task.status === "done"
                              ? { textDecoration: "line-through", color: "text.disabled" }
                              : {},
                          }}
                        />
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        label="Notes"
                        value={task.description || ""}
                        onChange={(e) => updateTaskLocal(task.id, { description: e.target.value })}
                        onBlur={() => commitTask(task.id)}
                        size="small"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 2 }}>
                      <TextField
                        label="Due"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={task.due_date ? task.due_date.slice(0, 10) : ""}
                        onChange={(e) => updateTaskLocal(task.id, { due_date: e.target.value })}
                        onBlur={() => commitTask(task.id)}
                        size="small"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 1 }}>
                      <Stack alignItems="flex-end">
                        <Tooltip title="Delete task">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deleteTaskMutation.isPending}
                            onClick={() => deleteTaskMutation.mutate(task.id)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
};
